/* SmartTriggers.jsx, bundle of every contextual surface that opens
   the MiniMatcher for the current page. Mount the parts you need
   anywhere; each part respects the page-exclusion rules + the
   context resolver, so the right rec lands per surface.

   Surfaces shipped together (Feb 2026 visibility upgrade):
     <SmartFab />           , floating action button (always visible)
     <SmartTopBanner />     , slim sticky top banner (dismissable per session)
     <SmartScrollToast />   , slide-in toast at 40% scroll (dismissable per session)
     <SmartExitIntent />    , desktop exit-intent popup (dismissable per session)
     <SmartRail />          , sticky desktop right-rail card on long-form guides
     <SmartMidArticle />    , inline card injected between content sections

   All trigger the SAME contextual MiniMatcher resolved from the URL +
   the user's profile (state / build_type / building_stage). Tradie
   nudges respect the New-Home suppression rule.

   Logged-in users: the existing Quick Actions `+` FAB lives bottom-
   right. We offset the SmartFab to bottom-LEFT for logged-in users so
   the two never collide. Anonymous users see the SmartFab bottom-
   right by default (industry-standard placement).
*/
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronRight, X, Hammer, Wrench, Briefcase, HardHat, Ruler, FileCheck, Home, PencilRuler, Compass, Search, MapPin, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MiniMatcher from './MiniMatcher';
import useSmartRec, { markFinanceSorted } from './useSmartRec';
import useAffiliateProfileActive from '../../hooks/useAffiliateProfileActive';
import './smartTriggers.css';

/* Category, lucide icon map. Mirrors the icons we already use inside
 * MiniMatcher so the FAB, banner, toast, etc. feel like the same
 * visual language as the matcher itself. We resolve by the part
 * after the colon in the category id (e.g. "professional:surveyor"
 * → "surveyor") and fall back to the super-cat icon, then Compass. */
const CATEGORY_ICON_MAP = {
  builder: Hammer,
  renovation_builder: Home,
  extension_builder: Home,
  pergola_builder: Home,
  deck_builder: Layers,
  shed_builder: HardHat,
  building_designer: PencilRuler,
  interior_designer: PencilRuler,
  surveyor: Ruler,
  town_planner: MapPin,
  energy_assessor: FileCheck,
  conveyancer: FileCheck,
  kitchens: Wrench,
  windows_doors: Wrench,
  house_inspect: FileCheck,
  equable: Briefcase,
};
function iconForCategory(category) {
  if (!category) return Compass;
  const [supKey, subKey] = String(category).split(':');
  if (subKey && CATEGORY_ICON_MAP[subKey]) return CATEGORY_ICON_MAP[subKey];
  if (supKey === 'professional') return Briefcase;
  if (supKey === 'tradie') return Wrench;
  if (supKey === 'builder') return Hammer;
  if (supKey === 'supplier') return Layers;
  if (supKey === 'partner') return Briefcase;
  return Compass;
}

/* Shared session-scoped dismissal store. We don't persist across
 * sessions on purpose, a user who dismissed the toast yesterday
 * should still see today's contextual nudge. */
const DISMISS_KEY = (kind, path) => `bp-smart-dismiss:${kind}:${(path || '/').toLowerCase()}`;
function isDismissed(kind, path) {
  try { return !!sessionStorage.getItem(DISMISS_KEY(kind, path)); } catch { return false; }
}
function dismiss(kind, path) {
  try { sessionStorage.setItem(DISMISS_KEY(kind, path), '1'); } catch { /* ignore */ }
}

/* The floating action button (SmartFab) is persistent across pages, so its
 * dismissal must persist too - otherwise it reappears on every navigation and
 * feels relentless. Once the user closes it we hide it site-wide for 14 days. */
const FAB_DISMISS_KEY = 'bp-smart-fab-dismissed-until';
const FAB_DISMISS_MS = 14 * 24 * 60 * 60 * 1000;
function isFabDismissed() {
  try {
    const until = Number(window.localStorage.getItem(FAB_DISMISS_KEY) || 0);
    return Date.now() < until;
  } catch { return false; }
}
function dismissFab() {
  try { window.localStorage.setItem(FAB_DISMISS_KEY, String(Date.now() + FAB_DISMISS_MS)); } catch { /* ignore */ }
}

/* Open the popup OR navigate when a `linkTo` is set. Mirror of the
 * logic in RecommendationStrip. */
function useRecAction(rec) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const trigger = () => {
    if (rec?.variant !== 'strip') return;
    if (rec.props?.linkTo) {
      navigate(rec.props.linkTo, { state: { from: rec.props.linkTo } });
    } else {
      setOpen(true);
    }
  };
  return { open, setOpen, trigger };
}

/* ════════════════════════════════════════════════════════════════
 * 1) SmartFab, floating action button on every marketing page.
 * ════════════════════════════════════════════════════════════════ */
export function SmartFab() {
  const { rec, pathname, isLoggedIn } = useSmartRec('fab', 'overlay');
  const { open, setOpen, trigger } = useRecAction(rec);
  const [hidden, setHidden] = useState(() => isFabDismissed());
  // Anonymous and logged-in users both get the right-side floating
  // matcher. The label + MiniMatcher category track the page topic
  // (resolved by `useSmartRec`) so a tiler page surfaces a tiler
  // matcher, a KDR page surfaces a KDR builder matcher, etc.
  //
  // We do still hide the FAB on a small set of pages where the
  // homepage hero + popups already own the same intent:
  //   - the marketing homepage  (build-type chips already surface this)
  //   - the matcher tool itself (would be a recursive popup)
  // Other exclusions like /privacy /terms /legal/* are already
  // handled inside the resolver (`isPathExcluded`).
  const HIDE_ON = [/^\/$/, /^\/workspace\/tools\/find-a-builder/, /^\/start/];
  const shouldRender = !hidden && !!rec && rec.variant === 'strip'
    && !HIDE_ON.some((re) => re.test(pathname || ''));

  // Mirror data-cookie-banner: expose body[data-smart-fab="1"] so
  // CSS can lift page-bottom content clear of the floating pill on
  // mobile (fixes CTAs/footer sitting under the FAB on Land Detail,
  // Building Disputes and similar long-scroll pages).
  useEffect(() => {
    if (!shouldRender) {
      document.body.removeAttribute('data-smart-fab');
      return undefined;
    }
    document.body.setAttribute('data-smart-fab', '1');
    return () => document.body.removeAttribute('data-smart-fab');
  }, [shouldRender]);

  if (!shouldRender) return null;
  // Mirror the resolver's CTA so the FAB label reflects the action
  // (e.g. "Find a builder", "Book an intro", "Find a surveyor")
  // instead of a generic "Get matched" that says nothing.
  const label = rec.props?.ctaLabel || 'Get matched';
  const FabIcon = iconForCategory(rec.props?.category);
  // Anonymous users → right side (homepage attention surface is empty).
  // Logged-in users have the workspace Quick Actions `+` FAB pinned
  // bottom-right, so park ours bottom-LEFT for them to avoid collisions.
  return ReactDOM.createPortal(
    <>
      <div className={`bp-smart-fab-wrap${isLoggedIn ? ' is-loggedin' : ''}`}>
        <button
          type="button"
          className="bp-smart-fab"
          onClick={trigger}
          data-testid="smart-fab"
          aria-label={label}
        >
          <FabIcon size={18} strokeWidth={2.4} />
          <span className="bp-smart-fab-label">{label}</span>
        </button>
        <button
          type="button"
          className="bp-smart-fab-x"
          onClick={() => { dismissFab(); setHidden(true); }}
          data-testid="smart-fab-dismiss"
          aria-label="Hide this suggestion"
          title="Hide"
        >
          <X size={13} strokeWidth={2.6} />
        </button>
      </div>
      <MiniMatcher
        open={open}
        onClose={() => setOpen(false)}
        category={rec.props.category}
        sourcePlacement={`fab-${(pathname || '/').replace(/[^a-z0-9]+/gi, '-').slice(0, 40)}`}
        sourcePage={pathname}
        initialState={rec.props.state}
        heading={rec.props.headline}
        subheading={rec.props.body}
        anchorSide={isLoggedIn ? 'left' : 'right'}
      />
    </>,
    document.body
  );
}

/* ════════════════════════════════════════════════════════════════
 * 2) SmartTopBanner, slim sticky banner at the very top.
 * ════════════════════════════════════════════════════════════════ */
export function SmartTopBanner() {
  const { rec, pathname, isLoggedIn } = useSmartRec('banner', 'overlay');
  const [dismissed, setDismissed] = useState(true);
  const ref = React.useRef(null);
  useEffect(() => { setDismissed(isDismissed('banner', pathname)); }, [pathname]);
  // Suppress entirely - the homepage hero + build-type popups +
  // Row-1 nav cluster already surface the same "Find a builder"
  // intent, the sticky top banner reads as noise. We keep the
  // component mounted (no Provider change required) but it returns
  // null so the rest of the smart-rec system stays in place.
  const isFindPath = pathname && pathname.startsWith('/find');
  const visible = false && !!rec && rec.variant === 'strip' && !dismissed && !isFindPath;
  // Push the page below the fixed banner by setting a body attribute
  // + a CSS var derived from the rendered banner height.
  useEffect(() => {
    if (!visible) {
      document.body.removeAttribute('data-smart-banner');
      return undefined;
    }
    document.body.setAttribute('data-smart-banner', '1');
    if (ref.current) {
      const h = ref.current.offsetHeight;
      document.body.style.setProperty('--bp-smart-banner-h', `${h}px`);
    }
    return () => {
      document.body.removeAttribute('data-smart-banner');
      document.body.style.removeProperty('--bp-smart-banner-h');
    };
  }, [visible]);
  const { open, setOpen, trigger } = useRecAction(rec);
  if (!visible) return null;
  return ReactDOM.createPortal(
    <>
      <div className="bp-smart-banner" data-testid="smart-banner" ref={ref}>
        <button type="button" className="bp-smart-banner-trigger" onClick={trigger}>
          <span className="bp-smart-banner-eyebrow">{rec.props.eyebrow}</span>
          <span className="bp-smart-banner-text">{rec.props.headline}</span>
          <span className="bp-smart-banner-cta">{rec.props.ctaLabel || 'Find out'} →</span>
        </button>
        {rec.props.allowFinanceSorted && (
          <button
            type="button"
            className="bp-smart-banner-alt"
            onClick={() => { markFinanceSorted(); dismiss('banner', pathname); setDismissed(true); }}
            data-testid="smart-banner-finance-sorted"
          >
            I already have finance sorted →
          </button>
        )}
        <button
          type="button"
          className="bp-smart-banner-close"
          aria-label="Dismiss"
          onClick={() => { dismiss('banner', pathname); setDismissed(true); }}
        >
          <X size={14} />
        </button>
      </div>
      <MiniMatcher
        open={open}
        onClose={() => setOpen(false)}
        category={rec.props.category}
        sourcePlacement={`banner-${(pathname || '/').replace(/[^a-z0-9]+/gi, '-').slice(0, 40)}`}
        sourcePage={pathname}
        initialState={rec.props.state}
        heading={rec.props.headline}
        subheading={rec.props.body}
        anchorSide={isLoggedIn ? 'left' : 'right'}
      />
    </>,
    document.body
  );
}

/* ════════════════════════════════════════════════════════════════
 * 3) SmartScrollToast, slide-in toast after 40% scroll.
 * ════════════════════════════════════════════════════════════════ */
export function SmartScrollToast() {
  const { rec, pathname, isLoggedIn } = useSmartRec('toast', 'overlay');
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(true);
  const { open, setOpen, trigger } = useRecAction(rec);

  useEffect(() => {
    setDismissed(isDismissed('toast', pathname));
    setVisible(false);
  }, [pathname]);

  useEffect(() => {
    if (dismissed) return undefined;
    const onScroll = () => {
      const h = document.documentElement;
      const pct = h.scrollHeight > h.clientHeight
        ? (h.scrollTop) / (h.scrollHeight - h.clientHeight)
        : 0;
      if (pct >= 0.4) setVisible(true);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  if (!rec || rec.variant !== 'strip' || dismissed || !visible) return null;

  return ReactDOM.createPortal(
    <>
      <aside className="bp-smart-toast" data-testid="smart-toast" role="status">
        <button
          type="button"
          className="bp-smart-toast-close"
          aria-label="Dismiss"
          onClick={() => { dismiss('toast', pathname); setVisible(false); }}
        >
          <X size={14} />
        </button>
        <p className="bp-smart-toast-eyebrow">{rec.props.eyebrow}</p>
        <p className="bp-smart-toast-headline">{rec.props.headline}</p>
        <button type="button" className="bp-smart-toast-btn" onClick={trigger}>
          {rec.props.ctaLabel || 'Find out'} <ChevronRight size={14} />
        </button>
        {rec.props.allowFinanceSorted && (
          <button
            type="button"
            className="bp-smart-toast-alt"
            onClick={() => { markFinanceSorted(); dismiss('toast', pathname); setVisible(false); }}
          >
            Already have finance sorted →
          </button>
        )}
      </aside>
      <MiniMatcher
        open={open}
        onClose={() => setOpen(false)}
        category={rec.props.category}
        sourcePlacement={`toast-${(pathname || '/').replace(/[^a-z0-9]+/gi, '-').slice(0, 40)}`}
        sourcePage={pathname}
        initialState={rec.props.state}
        heading={rec.props.headline}
        subheading={rec.props.body}
        anchorSide={isLoggedIn ? 'left' : 'right'}
      />
    </>,
    document.body
  );
}

/* ════════════════════════════════════════════════════════════════
 * 4) SmartExitIntent, desktop-only exit-intent popup.
 * ════════════════════════════════════════════════════════════════ */
export function SmartExitIntent() {
  const { rec, pathname, isLoggedIn } = useSmartRec('exit-intent', 'overlay');
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(true);
  const { open, setOpen, trigger } = useRecAction(rec);

  // Anonymous visitors should not see the right-side exit-intent
  // popup. The homepage hero + build-type popups + Row-1 nav already
  // surface the same "Find a builder" intent, and a desktop popup
  // that fires when the cursor leaves the viewport reads as noise.
  // Logged-in users keep it because their workspace puts the rec in
  // context of their current build stage.
  useEffect(() => {
    setDismissed(isDismissed('exit', pathname));
    setVisible(false);
  }, [pathname]);

  useEffect(() => {
    if (dismissed || !isLoggedIn) return undefined;
    // Desktop only, touch devices don't have a hover-out signal
    if (window.matchMedia && window.matchMedia('(hover: none)').matches) return undefined;
    let fired = false;
    const onLeave = (e) => {
      if (fired) return;
      if (e.clientY <= 0) { fired = true; setVisible(true); }
    };
    document.addEventListener('mouseout', onLeave);
    return () => document.removeEventListener('mouseout', onLeave);
  }, [dismissed, isLoggedIn]);

  if (!isLoggedIn) return null;
  if (!rec || rec.variant !== 'strip' || dismissed) return null;

  const close = () => { dismiss('exit', pathname); setVisible(false); };
  const showModal = visible && !open; // hide modal once the slide-up is open

  return ReactDOM.createPortal(
    <>
      {showModal && (
        <div className={`bp-smart-exit${isLoggedIn ? ' bp-smart-exit-left' : ' bp-smart-exit-right'}`} role="dialog" aria-modal="true" data-testid="smart-exit">
          <button type="button" className="bp-smart-exit-scrim" aria-label="Close" onClick={close} />
          <div className="bp-smart-exit-card">
            <button type="button" className="bp-smart-exit-close" aria-label="Close" onClick={close}>
              <X size={16} />
            </button>
            <p className="bp-smart-exit-eyebrow">{rec.props.eyebrow}</p>
            <h3 className="bp-smart-exit-headline">{rec.props.headline}</h3>
            <p className="bp-smart-exit-body">{rec.props.body}</p>
            <button type="button" className="bp-smart-exit-cta" onClick={() => { trigger(); setVisible(false); }}>
              {rec.props.ctaLabel || 'Find out'} <ChevronRight size={16} />
            </button>
            <button type="button" className="bp-smart-exit-text" onClick={close}>
              No thanks, I'll keep browsing
            </button>
          </div>
        </div>
      )}
      <MiniMatcher
        open={open}
        onClose={() => setOpen(false)}
        category={rec.props.category}
        sourcePlacement={`exit-intent-${(pathname || '/').replace(/[^a-z0-9]+/gi, '-').slice(0, 40)}`}
        sourcePage={pathname}
        initialState={rec.props.state}
        heading={rec.props.headline}
        subheading={rec.props.body}
        anchorSide={isLoggedIn ? 'left' : 'right'}
      />
    </>,
    document.body
  );
}

/* ════════════════════════════════════════════════════════════════
 * 5) SmartRail, sticky right-rail card on long-form guides
 *     (desktop ≥ 1100px). Caller wraps it inside the article column.
 * ════════════════════════════════════════════════════════════════ */
export function SmartRail() {
  const { rec, pathname, isLoggedIn } = useSmartRec('rail');
  const { open, setOpen, trigger } = useRecAction(rec);
  if (!rec || rec.variant !== 'strip') return null;
  return (
    <>
      <aside className="bp-smart-rail" data-testid="smart-rail">
        <p className="bp-smart-rail-eyebrow">{rec.props.eyebrow}</p>
        <h4 className="bp-smart-rail-headline">{rec.props.headline}</h4>
        <p className="bp-smart-rail-body">{rec.props.body}</p>
        <button type="button" className="bp-smart-rail-btn" onClick={trigger}>
          {rec.props.ctaLabel || 'Find out'} <ChevronRight size={14} />
        </button>
        {rec.props.allowFinanceSorted && (
          <button
            type="button"
            className="bp-smart-rail-alt"
            onClick={() => markFinanceSorted()}
          >
            Already have finance sorted →
          </button>
        )}
        <p className="bp-smart-rail-foot">Personally matched by BuildPilot.</p>
      </aside>
      <MiniMatcher
        open={open}
        onClose={() => setOpen(false)}
        category={rec.props.category}
        sourcePlacement={`rail-${(pathname || '/').replace(/[^a-z0-9]+/gi, '-').slice(0, 40)}`}
        sourcePage={pathname}
        initialState={rec.props.state}
        heading={rec.props.headline}
        subheading={rec.props.body}
        anchorSide={isLoggedIn ? 'left' : 'right'}
      />
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
 * 6) SmartMidArticle, inline card injected mid-article. Editorial
 *     pull-quote feel.
 * ════════════════════════════════════════════════════════════════ */
export function SmartMidArticle() {
  const { rec, pathname, isLoggedIn } = useSmartRec('mid-article');
  const { open, setOpen, trigger } = useRecAction(rec);
  if (!rec || rec.variant !== 'strip') return null;
  return (
    <>
      <aside className="bp-smart-mid" data-testid="smart-mid">
        <span className="bp-smart-mid-pill">{rec.props.eyebrow}</span>
        <p className="bp-smart-mid-headline">{rec.props.headline}</p>
        <p className="bp-smart-mid-body">{rec.props.body}</p>
        <button type="button" className="bp-smart-mid-btn" onClick={trigger}>
          {rec.props.ctaLabel || 'Find out'} <ChevronRight size={14} />
        </button>
        {rec.props.allowFinanceSorted && (
          <button
            type="button"
            className="bp-smart-mid-alt"
            onClick={() => markFinanceSorted()}
          >
            Already have finance sorted →
          </button>
        )}
      </aside>
      <MiniMatcher
        open={open}
        onClose={() => setOpen(false)}
        category={rec.props.category}
        sourcePlacement={`mid-${(pathname || '/').replace(/[^a-z0-9]+/gi, '-').slice(0, 40)}`}
        sourcePage={pathname}
        initialState={rec.props.state}
        heading={rec.props.headline}
        subheading={rec.props.body}
        anchorSide={isLoggedIn ? 'left' : 'right'}
      />
    </>
  );
}

/* All-in-one, drop a single <SmartTriggers /> at App root to mount
 * every site-wide surface (FAB + banner + exit-intent). The
 * page-specific surfaces (rail, mid-article) get dropped inline by
 * the page that wants them. The SmartScrollToast was removed
 * (Jun 2026), between the persistent FAB, the dismissible top
 * banner and the exit-intent popup, the scroll-triggered toast
 * was over-firing on long reads and adding friction without
 * lifting conversion. The component itself is still exported in
 * case we want to selectively re-introduce it on specific pages
 * later. */
function useCookieBannerProbe() {
  // Set body[data-cookie-banner="1"] while the BuildPilot cookie
  // consent banner is on screen so we can lift the mobile FAB clear
  // of it. The probe watches localStorage + DOM presence because the
  // consent component is mounted lazily and removes itself on choice.
  useEffect(() => {
    const apply = () => {
      try {
        const hasChoice = !!localStorage.getItem('buildpilot_cookie_consent');
        const live = !hasChoice && document.querySelector('[data-testid="cookie-consent-banner"]');
        if (live) document.body.setAttribute('data-cookie-banner', '1');
        else document.body.removeAttribute('data-cookie-banner');
      } catch { /* ignore */ }
    };
    apply();
    const id = window.setInterval(apply, 800);  // cheap poll; banner appears w/ ~3s delay
    return () => { window.clearInterval(id); document.body.removeAttribute('data-cookie-banner'); };
  }, []);
}

export default function SmartTriggers() {
  useCookieBannerProbe();
  const affiliateActive = useAffiliateProfileActive();
  if (affiliateActive) return null; // off-message on affiliate shopping pages
  return (
    <>
      <SmartFab />
      <SmartTopBanner />
      <SmartExitIntent />
    </>
  );
}
